import { memo, useCallback } from 'react';
import { Button, cn, Discount, Price } from '@divlab/divanui';
import { IconCheckThiny } from '@divlab/divanui/icons';

import * as Cart from '@Queries/Cart';
import { useDeps } from '@Contexts/DI';
import { useMeta } from '@Queries/Meta';
import useNavigation from '@Navigation/useNavigation';
import { useHasInCart } from '@Queries/Cart';
import useTranslation from '@Queries/useTranslation';
import Link from '@Navigation/Link';
import getProductName from '@Utils/getProductName';
import ProductImage from '@Components/ProductImage';
import styles from './ProductCard.module.css';

import type { HTMLAttributes, FC } from 'react';
import type { ProductData } from '@Types/Product';

interface ProductCardProps extends HTMLAttributes<HTMLDivElement> {
  product: ProductData;
}

const ProductCard: FC<ProductCardProps> = (props) => {
  const { className, product, ...restProps } = props;
  const { price, images } = product;
  const { t } = useTranslation();
  const hasExpired = price?.expired > 0;
  const hasDiscount = price?.discount > 0;
  const name = getProductName(product);
  const { openPage } = useNavigation();
  const hasInCart = useHasInCart({ shopProductId: product.id });
  const meta = useMeta();
  const { region } = meta.data;
  const { logger } = useDeps();
  const addToCart = Cart.useAddToCart();

  const handleError = useCallback(
    (err: unknown) => {
      logger.log(err);
    },
    [logger],
  );

  const handleBuy = useCallback(async () => {
    if (hasInCart) {
      openPage({ url: `${region.url}/order/check` });
      return;
    }

    if (!addToCart.isPending && !addToCart.isSuccess && !addToCart.isError) {
      addToCart.mutate(
        {
          products: [
            {
              shopProductId: product.id,
              quantity: 1,
              isModular: false,
            },
          ],
          options: { isRelated: false },
          analyticsLabel: 'Сопутствующие товары',
        },
        {
          onError: handleError,
        },
      );
    }
  }, [addToCart, handleError, hasInCart, openPage, product.id, region.url]);

  return (
    <div className={cn(styles.card, className)} {...restProps}>
      <Link to={product.link} target='_blank'>
        <ProductImage
          className={styles.imageContainer}
          cnImage={styles.image}
          src={images[0]?.src}
          alt={name}
        />
      </Link>

      <Link className={styles.name} to={product.link} target='_blank'>
        {name}
      </Link>

      <div className={styles.price}>
        <Price className={styles.actualPrice} price={product.price.actual} />

        {hasExpired && (
          <Price expired className={styles.expiredPrice} price={product.price.expired} />
        )}

        {hasDiscount && <Discount simple value={price.discount} fontSize={10} />}
      </div>
      <div className={styles.actions}>
        <Button
          className={styles.button}
          theme={hasInCart ? 'transparent' : 'primary'}
          wide
          waiting={addToCart.isPending}
          onClick={handleBuy}
          data-testid='add-button'
          before={hasInCart && <IconCheckThiny className={styles.iconCheck} />}
        >
          {hasInCart ? t('ui.in-cart-short') : t('ui.add-to-cart')}
        </Button>
      </div>
    </div>
  );
};

export default memo(ProductCard);
